import {
  type DealFormDataWithTargeting,
  type DealParticipant,
  RETARGETING_AQUA_VALIDATION_ERROR,
} from '@openx/types/deal.ts';
import type { OptionsMap } from '@openx/types/options.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import type { TestContext as YupTestContext } from 'yup';
import { hasRetargetingCapableDsp } from '../../targeting/utils/hasRetargetingCapableDsp.ts';
import { getRootFormValueFromContext } from './rboGroupValidation.ts';

export type RetargetingEnrichmentLookup = {
  mapById: OptionsMap;
  loading: boolean;
};

export const hasRetargetingSegmentApplied = (rootForm: DealFormDataWithTargeting | undefined): boolean => {
  const segment = rootForm?.targeting?.targeting?.[TargetingFields.RETARGETING];

  if (segment == null) {
    return false;
  }

  return segment.val != null && segment.val !== '';
};

/** Wait until OptionsByPath has resolved at least one selected DSP before blocking. */
const canEvaluateSelectedPartners = (
  dealParticipants: DealParticipant[] | null | undefined,
  mapById: OptionsMap
): boolean => {
  if (!Array.isArray(dealParticipants)) {
    return false;
  }

  return dealParticipants.some(participant => {
    const demandPartner = participant?.demand_partner;

    return !!demandPartner && mapById[demandPartner] != null;
  });
};

/**
 * Yup test for `__meta` (chained onto a clone of rboMetaSchema).
 * Blocks save when retargeting is applied and no selected DSP has retargeting enrichment.
 * `enrichmentLookup` is a mutable bag updated each render — same approach as nameValidation.
 */
export const createRetargetingAquaMetaTest = (enrichmentLookup: RetargetingEnrichmentLookup) => ({
  name: 'retargetingAquaDspValidation',
  test: function (this: YupTestContext) {
    const rootForm = getRootFormValueFromContext(this);
    const dealParticipants = rootForm?.deal_participants;
    const { mapById, loading } = enrichmentLookup;

    if (loading || !hasRetargetingSegmentApplied(rootForm)) {
      return true;
    }

    if (!canEvaluateSelectedPartners(dealParticipants, mapById)) {
      return true;
    }

    if (hasRetargetingCapableDsp(dealParticipants, mapById)) {
      return true;
    }

    return this.createError({
      message: RETARGETING_AQUA_VALIDATION_ERROR,
    });
  },
});
